<template>
  <div class="team-component">
    <h4 class="font-serif mb-3">
      {{ $t("dashboard.companyData.team.title") }}
    </h4>
    <ul class="list-group mb-3">
      <li
        v-for="account in accounts"
        :key="account.id"
        class="list-group-item d-flex justify-content-between align-items-center"
      >
        {{ account.name }} {{ account.email }}
        <button @click="confirmDelete(account)" class="btn btn-danger btn-sm">
          {{ $t("dashboard.companyData.team.delete") }}
        </button>
      </li>
    </ul>

    <!-- Invite to team section -->
    <h5 class="fs-8 fw-bold mb-3">
      {{ $t("dashboard.companyData.team.invite_title") }}
    </h5>

    <form @submit.prevent="inviteAccount">
      <div class="mb-3">
        <label for="newName" class="form-label">
          {{ $t("common.full_name") }}
        </label>
        <input
          id="newName"
          v-model="newName"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': v$.newName.$error }"
          :placeholder="$t('common.full_name')"
          @blur="v$.newName.$touch()"
        />
        <div v-if="v$.newName.$error" class="invalid-feedback">
          {{ v$.newName.$errors[0].$message }}
        </div>
      </div>

      <div class="mb-3">
        <label for="newEmail" class="form-label">
          {{ $t("common.email") }}
        </label>
        <input
          id="newEmail"
          v-model="newEmail"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': v$.newEmail.$error }"
          placeholder="example@email.com..."
          @blur="v$.newEmail.$touch()"
        />
        <div v-if="v$.newEmail.$error" class="invalid-feedback">
          {{ v$.newEmail.$errors[0].$message }}
        </div>
        <div v-if="allowedInviteDomains.length > 0" class="form-text">
          {{
            $t("dashboard.companyData.team.allowed_domains_hint", {
              domains: allowedInviteDomains.join(", "),
            })
          }}
        </div>
      </div>

      <!-- Send invite email checkbox -->
      <div class="form-check mb-3">
        <input
          class="form-check-input"
          type="checkbox"
          id="sendInviteEmail"
          v-model="sendInviteEmail"
        />
        <label class="form-check-label" for="sendInviteEmail">
          {{ $t("dashboard.companyData.team.send_invite_email") }}
        </label>
      </div>

      <!-- Untrusted companies cannot invite (server enforces this too); the wrapping span
           carries the tooltip because disabled buttons don't emit hover events. -->
      <span
        v-if="!isTrusted"
        :title="$t('dashboard.companyData.team.not_trusted_tooltip')"
        class="d-inline-block"
        tabindex="0"
      >
        <button type="submit" class="btn btn-primary" disabled>
          {{ $t("dashboard.companyData.team.invite") }}
        </button>
      </span>
      <button
        v-else
        type="submit"
        class="btn btn-primary"
        :disabled="v$.$invalid"
      >
        {{ $t("dashboard.companyData.team.invite") }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { getBranding } from "@/branding";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { Account, Company } from "@/types";
import { apiErrorMessage, apiErrorResponse } from "@/utils/apiErrors";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  company_id: number;
  company: Company;
}>();

const { t } = useI18n();
const validators = useValidators(t);
const { globalErrorHandler, globalWarningHandler } = useGlobalHandlers();

const isTrusted = computed(() => props.company.is_trusted === true);
// Admin-configured allowlist; empty means any domain may be invited. The backend enforces
// the same rule, this just tells the user up front which domains will be accepted. A
// structured server rejection carries the current list, which overrides the page-loaded one.
const serverAllowedInviteDomains = ref<string[] | null>(null);
const allowedInviteDomains = computed(
  () =>
    serverAllowedInviteDomains.value ??
    props.company.allowed_invite_domains ??
    []
);

const accounts = ref<Account[]>([]);
const newName = ref("");
const newEmail = ref("");
const sendInviteEmail = ref(true);

const trimmedName = computed(() => newName.value.trim());
const trimmedEmail = computed(() => newEmail.value.trim());

const emailDomain = (email: string) =>
  email.split("@").pop()?.toLowerCase() ?? "";

const domainNotAllowedMessage = () =>
  t("dashboard.companyData.team.domain_not_allowed", {
    domains: allowedInviteDomains.value.join(", "),
  });

const allowedDomain = helpers.withMessage(
  domainNotAllowedMessage,
  (value: string) =>
    allowedInviteDomains.value.length === 0 ||
    allowedInviteDomains.value.includes(emailDomain(value))
);

const rules = {
  newName: {
    required: validators.required,
    minLength: validators.minLength(2),
  },
  newEmail: {
    required: validators.required,
    email: validators.email,
    allowedDomain,
  },
};

const v$ = useVuelidate(
  rules,
  {
    newName: trimmedName,
    newEmail: trimmedEmail,
  },
  { $scope: "team" }
);

// A refetched company carries the authoritative list again; drop the rejection override
// so it cannot outlive the data it corrected.
watch(
  () => props.company.allowed_invite_domains,
  () => {
    serverAllowedInviteDomains.value = null;
  }
);

const fetchAccounts = async () => {
  try {
    const response = await goSolidApi.get(
      `/api/company/${props.company_id}/accounts`
    );
    accounts.value = response.data.accounts as Account[];
  } catch (error) {
    globalErrorHandler(error);
  }
};

const confirmDelete = async (account: Account) => {
  if (
    confirm(
      t("dashboard.companyData.team.confirmDelete", {
        account_name: account.name,
        company_name: props.company.name,
      })
    )
  ) {
    try {
      await goSolidApi.delete(
        `/api/company/${props.company_id}/account/${account.id}`
      );
      await fetchAccounts();
    } catch (error) {
      globalErrorHandler(error);
    }
  }
};

const inviteAccount = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    await goSolidApi.post(`/api/company/${props.company_id}/account`, {
      name: trimmedName.value,
      email: trimmedEmail.value,
      send_invite_email: sendInviteEmail.value,
      // The invitation's join link points back at the portal this UI runs on, so the
      // invitee lands on the same environment as their team (Soldera app / EKOenergy
      // registry).
      portal: getBranding().key,
    });
    newName.value = "";
    newEmail.value = "";
    sendInviteEmail.value = true;
    v$.value.$reset();
    await fetchAccounts();
  } catch (error) {
    if (isInviteDomainNotAllowedError(error)) {
      // The allowlist changed after this page loaded; adopt the server's current list so
      // the hint and validation match it, and show its message, which the generic error
      // banner would not.
      serverAllowedInviteDomains.value =
        error.response.data.error_data.allowed_domains;
      globalWarningHandler(
        t("dashboard.companyData.team.invite_title"),
        error.response.data.message
      );
      return;
    }
    if (isAccountAlreadyMemberError(error)) {
      // A colleague added this person since the page loaded; refetch so the roster shows them.
      globalWarningHandler(
        t("dashboard.companyData.team.invite_title"),
        apiErrorMessage(error, t("dashboard.companyData.team.invite_title"))
      );
      await fetchAccounts();
      return;
    }
    globalErrorHandler(error);
  }
};

const isAccountAlreadyMemberError = (error: unknown) =>
  apiErrorResponse(error)?.data?.error_type === "ACCOUNT_ALREADY_MEMBER";

interface InviteDomainNotAllowedResponse {
  response: {
    data: {
      error_type: "INVITE_DOMAIN_NOT_ALLOWED";
      message: string;
      error_data: { allowed_domains: string[] };
    };
  };
}

const isInviteDomainNotAllowedError = (
  error: unknown
): error is InviteDomainNotAllowedResponse => {
  const data = (error as Partial<InviteDomainNotAllowedResponse>)?.response
    ?.data;
  return (
    data?.error_type === "INVITE_DOMAIN_NOT_ALLOWED" &&
    typeof data.message === "string" &&
    Array.isArray(data.error_data?.allowed_domains)
  );
};

onMounted(fetchAccounts);
</script>
